import { ErrorHandler, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { IS_VIEW_ALL_ROUTE } from '../constants/menu-structure-route-data';
import { ModuleNumbers } from '../enums/module-numbers.enum';
import { MenuItemModel } from '../models/menu-id.model';
import { ResetCurrentFilter, RestoreCurrentFilter } from '../state/main-table-filter.actions';
import { MainTableFilterState } from '../state/main-table-filter.state';
import {
  ClearMenuSelection,
  RestoreMenuSelection,
  SelectMenuStructureNode,
} from '../state/menu-tree-structure.actions';
import { MenuTreeStructureState } from '../state/menu-tree-structure.state';
import { MenuListLogicService, MenuModuleRoutes } from './menu-list-logic.service';

export type MenuNavigationOutcome = 'navigated' | 'same-target' | 'cancelled' | 'failed';

@Injectable({ providedIn: 'root' })
export class MenuStructureNavigationService {
  private activeTransaction: symbol | null = null;

  constructor(
    private readonly router: Router,
    private readonly errorHandler: ErrorHandler,
    private readonly menuListLogicService: MenuListLogicService,
    private readonly store: Store
  ) {}

  getNodeUrl(module: ModuleNumbers, nodeId: string): string | null {
    const routes = this.menuListLogicService.getModuleRoutes(module);
    if (!routes?.selectedRoot) {
      return null;
    }
    const nodeUrl = routes.appendNodeId ? `${routes.selectedRoot}/${nodeId}` : routes.selectedRoot;
    const targetUrl = routes.defaultChildRoute ? `${nodeUrl}/${routes.defaultChildRoute}` : nodeUrl;
    return this.withPreservedQueryParams(targetUrl, routes);
  }

  private getViewAllUrl(module: ModuleNumbers, selectedNodeId: string): string | null {
    const routes = this.menuListLogicService.getModuleRoutes(module);
    if (!routes?.selectedRoot || !routes.viewAllRoot) {
      return null;
    }
    const currentUrl = this.getCurrentPath();
    if (!selectedNodeId) {
      return this.withPreservedQueryParams(
        this.isViewAllRoute() ? currentUrl : routes.viewAllRoot,
        routes
      );
    }
    if (!routes.preserveNestedRoute) {
      return this.withPreservedQueryParams(routes.viewAllRoot, routes);
    }
    const selectedNodeUrl = `${routes.selectedRoot}/${selectedNodeId}`;
    const nestedRoute = currentUrl.startsWith(selectedNodeUrl)
      ? currentUrl.slice(selectedNodeUrl.length)
      : '';
    return this.withPreservedQueryParams(`${routes.viewAllRoot}${nestedRoute}`, routes);
  }

  private async navigate(url: string): Promise<MenuNavigationOutcome> {
    try {
      const targetUrl = this.router.parseUrl(url);
      if (this.isCurrentUrl(targetUrl)) {
        return 'same-target';
      }
      return (await this.router.navigateByUrl(targetUrl)) ? 'navigated' : 'cancelled';
    } catch (error) {
      this.errorHandler.handleError(error);
      return 'failed';
    }
  }

  async selectNode(
    module: ModuleNumbers,
    node: Pick<MenuItemModel, 'id' | 'name'>
  ): Promise<MenuNavigationOutcome | null> {
    const targetUrl = this.getNodeUrl(module, node.id);
    if (!targetUrl) {
      return null;
    }
    const transaction = this.startTransaction();
    if (!transaction) {
      return null;
    }
    try {
      return await this.performNodeSelection(node, targetUrl);
    } finally {
      this.finishTransaction(transaction);
    }
  }

  async selectAll(
    module: ModuleNumbers,
    selectedNodeId: string
  ): Promise<MenuNavigationOutcome | null> {
    const targetUrl = this.getViewAllUrl(module, selectedNodeId);
    if (!targetUrl) {
      return null;
    }
    const transaction = this.startTransaction();
    if (!transaction) {
      return null;
    }
    try {
      const previousCurrentFilter = cloneDeep(
        this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails)
      );
      await firstValueFrom(this.store.dispatch(new ResetCurrentFilter()));
      const outcome = await this.navigate(targetUrl);
      if (outcome !== 'navigated') {
        await firstValueFrom(this.store.dispatch(new RestoreCurrentFilter(previousCurrentFilter)));
      }
      if (this.isCompleted(outcome)) {
        await firstValueFrom(this.store.dispatch(new ClearMenuSelection()));
      }
      return outcome;
    } finally {
      this.finishTransaction(transaction);
    }
  }

  isCompleted(outcome: MenuNavigationOutcome | null): boolean {
    return outcome === 'navigated' || outcome === 'same-target';
  }

  private startTransaction(): symbol | null {
    if (this.activeTransaction) {
      return null;
    }
    this.activeTransaction = Symbol('menu-navigation');
    return this.activeTransaction;
  }

  private finishTransaction(transaction: symbol): void {
    if (this.activeTransaction === transaction) {
      this.activeTransaction = null;
    }
  }

  isViewAllRoute(): boolean {
    const activeRoute = this.getDeepestRoute(this.router.routerState.snapshot.root);
    return activeRoute.pathFromRoot.some((route) => route.data[IS_VIEW_ALL_ROUTE] === true);
  }

  private async performNodeSelection(
    node: Pick<MenuItemModel, 'id' | 'name'>,
    targetUrl: string
  ): Promise<MenuNavigationOutcome> {
    const previousCurrentFilter = cloneDeep(
      this.store.selectSnapshot(MainTableFilterState.getCurrentFilterDetails)
    );
    const previousActiveElementId = this.store.selectSnapshot(
      MenuTreeStructureState.activeMenuElementId
    );
    const previousTableData = cloneDeep(
      this.store.selectSnapshot(MenuTreeStructureState.getSelectedPartOfMenuStructure)
    );
    const previousDisplayName = this.store.selectSnapshot(MenuTreeStructureState.getDisplayName);
    await firstValueFrom(
      this.store.dispatch([
        new ResetCurrentFilter(),
        new SelectMenuStructureNode(node.id, node.name),
      ])
    );
    const outcome = await this.navigate(targetUrl);
    if (!this.isCompleted(outcome)) {
      await firstValueFrom(
        this.store.dispatch([
          new RestoreMenuSelection(previousActiveElementId, previousTableData, previousDisplayName),
          new RestoreCurrentFilter(previousCurrentFilter),
        ])
      );
      return outcome;
    }
    if (outcome === 'same-target') {
      await firstValueFrom(this.store.dispatch(new RestoreCurrentFilter(previousCurrentFilter)));
    }
    return outcome;
  }

  private getCurrentPath(): string {
    return this.router.serializeUrl(this.router.parseUrl(this.router.url)).split(/[?#]/)[0];
  }

  private withPreservedQueryParams(url: string, routes: MenuModuleRoutes): string {
    if (!routes.preservedQueryParams?.length) {
      return url;
    }
    const targetUrl = this.router.parseUrl(url);
    const currentUrl = this.router.parseUrl(this.router.url);
    routes.preservedQueryParams.forEach((key) => {
      if (currentUrl.queryParams[key] !== undefined) {
        targetUrl.queryParams[key] = currentUrl.queryParams[key];
      }
    });
    return this.router.serializeUrl(targetUrl);
  }

  private isCurrentUrl(targetUrl: UrlTree): boolean {
    const currentUrl = this.router.parseUrl(this.router.url);
    return this.router.serializeUrl(targetUrl) === this.router.serializeUrl(currentUrl);
  }

  private getDeepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    let deepestRoute = route;
    while (deepestRoute.firstChild) {
      deepestRoute = deepestRoute.firstChild;
    }
    return deepestRoute;
  }
}
